import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMeta,
  DataTablePageEvent,
} from 'primereact/datatable';
import { FilterMatchMode } from 'primereact/api';
import { renderCell } from '../../component/data-table/operations/render-cell';
import { getCellForCourseStatus } from '../programs/program/courses/operations/get-cell-for-course-status';
import { CoursesFilter, CourseFilters } from './courses-filter/courses-filter';
import { getCoursesListOperation } from './operations/get-courses-list-operation';
import dateFormat from '../../utils/date-format';
import navigationManager from '../../utils/navigation-manager';
import { ReportCoursesType } from '../../api/types/reports/education/courses/report-courses.type';
import { TableColumnLongTextWithInformation } from '../../component/table-componenets/cusom-column/table-column-long-text-with-information';
import { toast } from 'react-toastify';
import { Button } from 'primereact/button';
import { handleExportCoursesToExcel } from './operations/export-courses-to-excel-operation';

interface CoursesListPageProps {
  allowNavigation?: boolean;
  onCourseClick?: (course: ReportCoursesType) => any;
}

export const CoursesListPage: React.FC<CoursesListPageProps> = ({
  allowNavigation = true,
  onCourseClick,
}) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<ReportCoursesType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Pagination state
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  // Store current filters for pagination
  const currentFiltersRef = useRef<CourseFilters | null>(null);

  useEffect(() => {
    if (allowNavigation) {
      navigationManager.setNavigate(navigate);
    }
  }, [navigate, allowNavigation]);

  // Fetch courses with current pagination parameters
  const fetchCourses = async (
    filters: CourseFilters,
    newOffset: number,
    newLimit: number,
  ) => {
    await getCoursesListOperation(
      filters,
      newOffset,
      newLimit,
      setLoading,
      setCourses,
      setTotal,
    );
  };

  const handleRowClick = (rowData: ReportCoursesType) => {
    if (!allowNavigation && onCourseClick) {
      onCourseClick(rowData);
      return;
    }

    if (rowData.courseProgramYear === null) {
      toast.info('קורס זה נמצא ביתרה לניצול, יש לשייך לשנה טרם הכניסה לקורס');
    } else {
      navigationManager.navigateToCourse(
        rowData.branchId,
        rowData.programs[0].programId,
        rowData.courseId,
      );
    }
  };

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    courseName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    branchName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    majorName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    abstractProgramName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const handleFilterChange = (courseFilters: CourseFilters) => {
    // Store current filters for pagination
    currentFiltersRef.current = courseFilters;
    // Reset to first page when filters change
    setOffset(0);
    fetchCourses(courseFilters, 0, limit);
  };

  // Handle page change from paginator
  const onPageChange = (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    if (currentFiltersRef.current) {
      fetchCourses(currentFiltersRef.current, newOffset, newLimit);
    }
  };

  const formatLecturers = (lecturers: any[]) => {
    return (
      lecturers?.map((l) => `${l.firstName} ${l.lastName}`).join(', ') || ''
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '20px',
        paddingTop: '16px',
        height: 'calc(100vh - 80px)',
      }}
    >
      {/* Filter Section */}
      <div
        style={{
          height: 'calc(100vh - 72px)',
          width: '300px',
          minWidth: '250px',
        }}
      >
        <CoursesFilter onFilterChange={handleFilterChange} />
      </div>

      {/* Table Section */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            color: '#041E49',
            fontSize: '22px',
            fontWeight: 'bold',
            marginBottom: '20px',
          }}
        >
          רשימת קורסים
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '8px',
          }}
        >
          <label style={{ fontSize: '14px', color: '#495057' }}>
            מספר שורות: {total}
          </label>
          <Button
            style={{
              height: '30px',
              width: '30px',
              backgroundColor: '#10b981',
              borderColor: '#10b981',
            }}
            icon="pi pi-file-excel"
            tooltip="ייצוא לאקסל"
            onClick={() => handleExportCoursesToExcel(courses)}
          />
        </div>

        <DataTable
          value={courses}
          loading={loading}
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          dataKey="courseId"
          tableStyle={{
            direction: 'rtl',
            cursor: allowNavigation ? 'pointer' : 'default',
          }}
          filterDisplay="row"
          filters={filters}
          onFilter={(e) => setFilters(e.filters)}
          scrollable
          scrollHeight="calc(100vh - 300px)"
          onRowClick={(e) => handleRowClick(e.data as ReportCoursesType)}
          paginator
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          lazy={true}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
          <Column
            field="branchName"
            header="שלוחה"
            filter
            filterPlaceholder="חפש שלוחה"
            style={{ textAlign: 'right', maxWidth: '108px' }}
          />
          <Column
            field="courseName"
            header="שם קורס"
            filter
            filterPlaceholder="חפש קורס"
            style={{
              textAlign: 'right',
              maxWidth: '108px',
            }}
          />
          <Column
            field="additionalCourseName"
            header="שם נוסף"
            filter
            filterPlaceholder="חפש קורס"
            style={{
              textAlign: 'right',
              maxWidth: '108px',
            }}
          />
          {/*          <Column
            field="majorName"
            header="מגמה"
            filter
            filterPlaceholder="חפש מגמה"
            style={{ textAlign: 'right' }}
          />*/}
          <Column
            field="abstractProgramName"
            header="שם תכנית"
            filter
            filterPlaceholder="חפש תכנית"
            style={{ textAlign: 'right' }}
            body={(rowData: ReportCoursesType) => {
              const firstProgram = rowData.programs?.[0];
              return firstProgram
                ? `${firstProgram.abstractProgramName} - מחזור ${firstProgram.annualSerialProgramNumber} - ${firstProgram.programStartYear}`
                : '';
            }}
          />
          {/*<Column
            field="programStartYear"
            header="שנת התחלה"
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            field="annualSerialProgramNumber"
            header="מספר סידורי בשנה"
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            field="abstractProgramYears"
            header="שנה בתכנית"
            sortable
            style={{ textAlign: 'right' }}
            body={(rowData) => `שנה ${rowData.abstractProgramYears}'`}
          />*/}
          <Column
            field="courseStartDate"
            header="תאריך התחלה"
            sortable
            style={{ textAlign: 'right' }}
            body={(rowData: ReportCoursesType) => (
              <label>
                {rowData.courseStartDate
                  ? dateFormat.displayFormatDate(rowData.courseStartDate)
                  : 'טרם שובצו הרצאות'}
              </label>
            )}
          />
          <Column
            field="status"
            header="סטטוס קורס"
            style={{ textAlign: 'right' }}
            body={(rowData) =>
              renderCell(getCellForCourseStatus(rowData.status))
            }
          />
          <Column
            field="courseHours"
            header="מכסת שעות מקורית"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="capacityHours"
            header="מכסת שעות עדכנית"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />

          <Column
            field="scheduledHours"
            header="סך שעות ששובצו"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="hoursActuallyWorked"
            header="סך שעות שבוצעו"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="attendancePercentage"
            header="אחוז נוכחות"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="studentNumber"
            header="מספר תלמידים"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="averageFinalGrade"
            header="ציון סופי ממוצע"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="lecturers"
            header="מרצים"
            style={{ textAlign: 'right' }}
            body={(rowData) => formatLecturers(rowData.lecturers)}
          />
          <Column
            field="courseNotes"
            header="הערות הקורס"
            style={{ textAlign: 'right' }}
            body={(rowData: ReportCoursesType) => (
              <TableColumnLongTextWithInformation
                text={rowData.courseNotes}
                uniqueId={rowData.courseId}
                maxLength={8}
              />
            )}
          />
        </DataTable>
      </div>
    </div>
  );
};
